/* eslint-disable @typescript-eslint/no-explicit-any */
import { FetchHandlerTypes } from 'types/fetchhandlertypes';

export class FetchHandler {

    private url ?: string;

    private method : FetchHandlerTypes.ACCEPTED_HTTP_REQUESTS = 'POST';

    private contentType : FetchHandlerTypes.ACCEPTED_MIME_TYPES = 'application/x-www-form-urlencoded';

    private headers : Record<string, any> = {};

    private body : Record<string, any> = {};

    private fetchingInProgress : boolean = false;

    private mode : FetchHandlerTypes.ACCEPTED_MODES = 'same-origin';

    private _abortController : AbortController = new AbortController();

    public static create() : FetchHandler {
        return new FetchHandler();
    }

    public setUrl(url : string) : this {
        this.url = url;
        return this;
    }

    /**
    * Set the method of the request
    * This is optional, the default value is 'POST'
    * @param method
    */
    public setMethod(method : FetchHandlerTypes.ACCEPTED_HTTP_REQUESTS) : this {
        this.method = method;
        return this;
    }

    /**
    * Set a header for the request
    * This is optional, the default value is an empty object
    * @param key
    * @param value
    */
    public setHeader(key : string, value : string) : this {
        this.headers[key] = value;
        return this;
    }

    /**
    * Set the content type of the request
    * This is optional, the default value is 'application/json'
    * @param contentType
    */
    public setContentType(contentType : FetchHandlerTypes.ACCEPTED_MIME_TYPES) : this {
        this.contentType = contentType;
        return this;
    }

    /**
    * Set the body of the request
    * This is optional, the default value is an empty object
    * @param body
    */
    public setBody(body : any) : this {
        this.body = body;
        return this;
    }

    public abort() : this {
        this._abortController.abort();
        return this;
    }

    /**
     * Check if the request is in progress
     * @returns {boolean}
     */
    public isFetching() : boolean {
        return this.fetchingInProgress;
    }

    public setXMLHttpRequest() : this {
        this.headers = {
            ...this.headers,
            'X-Requested-With': 'XMLHttpRequest',
        };
        return this;
    }

    public setMode(mode : FetchHandlerTypes.ACCEPTED_MODES) : this {
        this.mode = mode;
        return this;
    }

    /**
    * @returns {Promise<any>}
    */
    public async send() : Promise<any> {
        this.initCSRFToken();
        this.initContentType();

        const options = {
            method: this.method,
            headers: this.headers,
            body: this.body && this.method !== 'GET' ? JSON.stringify(this.body) : null,
            signal: this._abortController.signal,
            mode: this.mode,
        };

        try {
            this.fetchingInProgress = true;
            const response = await fetch(this.url as string, options as RequestInit);
            this.fetchingInProgress = false;

            /**
             * No-cors causes "opaque" type response, so we can't get
             * any response body from it.
             */
            if (this.mode !== 'no-cors') {
                const jsonResponse = await response.json();
                if (response.status >= 300) {
                    this.reportToSentry(jsonResponse);
                }
                return jsonResponse;
            }
        }
        catch (error) {
            this.fetchingInProgress = false;

            if (error instanceof Error) {
                throw {
                    errors: {
                        network: 'Network error',
                    },
                };
            }
            throw error;
        }
    }

    /**
     * Reports the failed response/error to Sentry without ever throwing itself,
     * so a Sentry SDK issue (or it being blocked/absent) can't interrupt the caller's error handling.
     * @private
     */
    private reportToSentry(error : unknown) : void {
        try {
            const exception = error instanceof Error
                ? error
                : new Error(`FetchHandler request failed: ${this.method} ${this.url}`);

            window.Sentry?.captureException(exception, {
                extra: {
                    url: this.url,
                    method: this.method,
                    response: error instanceof Error ? undefined : error,
                },
            });
        }
        catch {
            // Intentionally ignored, see method docblock.
        }
    }

    /**
    * Initialize the CSRF token
    * Internal method, should not be called from outside
    * @private
    */
    private initCSRFToken() : void {
        const token = document.head.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        this.headers = {
            ...this.headers,
            'X-CSRF-TOKEN': token ?? '',
        };
    }

    /**
    * @private
    */
    private initContentType() : void {
        this.setHeader('Content-Type', `${this.contentType};charset=UTF-8`);
    }

}
